export type CatalogNodeType =
  | 'controller'
  | 'bus'
  | 'device'
  | 'point'
  | 'timer'
  | 'calc'
  | 'label'
  | 'mqtt_topic'
  | 'zigbee_point'
  | 'dashboard'
  | 'threshold'
  | 'notify'
  | 'toggle';

export type CatalogNode = {
  id: string;
  type: CatalogNodeType;
  x: number;
  y: number;
  owner?: string;
  hostname?: string;
  of?: string;
  key?: string;
  poll?: boolean;
  interval?: number;
  interval_ms?: number;
  op?: string;
  value?: number;
  layout?: string;
  widget?: string;
  connection?: string;
  topic?: string;
  widgetType?: string;
  title?: string;
  signalName?: string;
  /** stacklight device: definition (signal file id); may be shared by many instances. */
  signal?: string;
  icon?: string;
  busType?: string;
  role?: string;
  deviceType?: string;
  pin?: number;
  mode?: string;
  inverted?: boolean;
  edge?: string;
  /** threshold: breach below this */
  min?: number;
  /** threshold: breach above this */
  max?: number;
  hysteresis?: number;
  alarmDelayMin?: number;
  resetDelayMin?: number;
  /** threshold: payload text that raises the alarm; empty keeps min/max */
  match?: string;
  /** threshold: optional path into the MQTT payload; empty reads the message text */
  field?: string;
  criticality?: number;
  /** notify: pushover / mail (secrets stay in alerts.json channels) */
  channels?: string[];
  /** dashboard relay: name per ch1…ch8, index 0 = ch1 */
  channelNames?: string[];
  /** dashboard relay: true = show a bulb and do not command that channel */
  channelReadOnly?: boolean[];
};

export type CatalogEdge = {
  from: string;
  to: string;
  /** State and command share this cable. The reverse of `from → to` is implied. */
  both?: boolean;
};

export type CatalogFlow = {
  id: string;
  enable: boolean;
  layouts?: string[];
  nodes: CatalogNode[];
  edges: CatalogEdge[];
};

export const CATALOG_NODE_TYPES: CatalogNodeType[] = [
  'controller', 'bus', 'device', 'point', 'timer', 'calc', 'label', 'mqtt_topic', 'zigbee_point', 'dashboard',
  'threshold', 'notify', 'toggle',
];

export function isCatalogNodeType(value: string): value is CatalogNodeType {
  return (CATALOG_NODE_TYPES as string[]).includes(value);
}

export function defaultCatalogFlow(id = 'default'): CatalogFlow {
  return { id, enable: true, layouts: [], nodes: [], edges: [] };
}

export function isCatalogFlowPayload(raw: unknown): boolean {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return false;
  }
  const nodes = (raw as { nodes?: unknown }).nodes;
  if (!Array.isArray(nodes)) {
    return false;
  }
  if (nodes.length === 0) {
    return Array.isArray((raw as { edges?: unknown }).edges);
  }
  const first = nodes[0];
  return Boolean(first && typeof first === 'object' && isCatalogNodeType(String((first as { type?: unknown }).type || '')));
}

function asNode(raw: unknown): CatalogNode | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const item = raw as Record<string, unknown>;
  const type = String(item.type || '');
  if (!isCatalogNodeType(type)) {
    return null;
  }
  const id = String(item.id || '').trim();
  if (!id) {
    return null;
  }
  const node: CatalogNode = {
    id,
    type,
    x: Number(item.x) || 0,
    y: Number(item.y) || 0,
  };
  if (typeof item.owner === 'string' && item.owner) node.owner = item.owner;
  if (typeof item.hostname === 'string' && item.hostname) node.hostname = item.hostname;
  if (typeof item.of === 'string' && item.of) node.of = item.of;
  if (typeof item.key === 'string' && item.key) node.key = item.key;
  if (typeof item.poll === 'boolean') node.poll = item.poll;
  if (item.interval != null) node.interval = Number(item.interval) || 0;
  if (item.interval_ms != null) node.interval_ms = Number(item.interval_ms) || 0;
  if (typeof item.op === 'string' && item.op) node.op = item.op;
  if (item.value != null && item.value !== '') node.value = Number(item.value);
  if (typeof item.layout === 'string') node.layout = item.layout;
  if (typeof item.widget === 'string') node.widget = item.widget;
  if (typeof item.connection === 'string') node.connection = item.connection;
  if (typeof item.topic === 'string') node.topic = item.topic;
  if (typeof item.widgetType === 'string') node.widgetType = item.widgetType;
  if (typeof item.title === 'string') node.title = item.title;
  if (typeof item.signalName === 'string' && item.signalName) node.signalName = item.signalName;
  if (typeof item.signal === 'string' && item.signal) node.signal = item.signal;
  if (typeof item.icon === 'string' && item.icon) node.icon = item.icon;
  if (typeof item.busType === 'string' && item.busType) node.busType = item.busType;
  if (typeof item.role === 'string' && item.role) node.role = item.role;
  if (typeof item.deviceType === 'string' && item.deviceType) node.deviceType = item.deviceType;
  if (item.pin != null && item.pin !== '') node.pin = Number(item.pin);
  if (typeof item.mode === 'string' && item.mode) node.mode = item.mode;
  if (typeof item.inverted === 'boolean') node.inverted = item.inverted;
  if (typeof item.edge === 'string' && item.edge) node.edge = item.edge;
  if (item.min != null && item.min !== '') node.min = Number(item.min);
  if (item.max != null && item.max !== '') node.max = Number(item.max);
  if (item.hysteresis != null && item.hysteresis !== '') node.hysteresis = Number(item.hysteresis);
  if (item.alarmDelayMin != null && item.alarmDelayMin !== '') node.alarmDelayMin = Number(item.alarmDelayMin);
  if (item.resetDelayMin != null && item.resetDelayMin !== '') node.resetDelayMin = Number(item.resetDelayMin);
  if (typeof item.match === 'string' && item.match) node.match = item.match;
  if (typeof item.field === 'string' && item.field) node.field = item.field;
  if (item.criticality != null && item.criticality !== '') node.criticality = Number(item.criticality);
  if (Array.isArray(item.channels)) {
    node.channels = item.channels.map((c) => String(c || '').trim()).filter(Boolean);
  }
  if (Array.isArray(item.channelNames)) {
    node.channelNames = item.channelNames.map((c) => String(c ?? ''));
  }
  if (Array.isArray(item.channelReadOnly)) {
    node.channelReadOnly = item.channelReadOnly.map((c) => c === true);
  }
  return normalizeCatalogNode(node);
}

function asEdge(raw: unknown): CatalogEdge | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const item = raw as Record<string, unknown>;
  const from = String(item.from || '').trim();
  const to = String(item.to || '').trim();
  if (!from || !to || !from.includes('.') || !to.includes('.')) {
    return null;
  }
  const edge: CatalogEdge = { from, to };
  if (item.both === true) {
    edge.both = true;
  }
  return edge;
}

export function parseCatalogFlow(raw: unknown, fallbackId = 'default'): { doc: CatalogFlow; legacy: boolean } {
  const wrapped = raw && typeof raw === 'object' && !Array.isArray(raw)
    ? raw as Record<string, unknown>
    : {};
  const obj = isCatalogFlowPayload(wrapped)
    ? wrapped
    : (wrapped.flow && isCatalogFlowPayload(wrapped.flow) ? wrapped.flow as Record<string, unknown> : wrapped);
  if (!isCatalogFlowPayload(obj)) {
    return { doc: defaultCatalogFlow(typeof obj.id === 'string' ? obj.id : fallbackId), legacy: true };
  }
  const nodes = (obj.nodes as unknown[]).map(asNode).filter((item): item is CatalogNode => Boolean(item));
  const edges = Array.isArray(obj.edges)
    ? (obj.edges as unknown[]).map(asEdge).filter((item): item is CatalogEdge => Boolean(item))
    : [];
  const layouts = Array.isArray(obj.layouts)
    ? obj.layouts.map((item) => String(item || '').trim()).filter(Boolean)
    : [];
  return {
    legacy: false,
    doc: {
      id: typeof obj.id === 'string' && obj.id ? obj.id : fallbackId,
      enable: obj.enable !== false,
      layouts,
      nodes,
      edges,
    },
  };
}

/** Function fields only. Ports live in `edges[]`; firmware walks those, not copied topics. */
const NODE_FIELDS: Record<CatalogNodeType, readonly (keyof CatalogNode)[]> = {
  controller: ['hostname', 'owner'],
  bus: ['of', 'busType', 'role'],
  device: ['of', 'deviceType', 'signal', 'hostname'],
  point: ['of', 'key', 'poll', 'pin', 'mode', 'inverted', 'edge', 'hostname'],
  timer: ['interval', 'interval_ms'],
  calc: ['op', 'value'],
  label: ['layout', 'widget'],
  mqtt_topic: ['topic'],
  zigbee_point: ['of', 'key'],
  dashboard: ['widgetType', 'title', 'signalName', 'icon', 'channelNames', 'channelReadOnly'],
  threshold: ['min', 'max', 'hysteresis', 'alarmDelayMin', 'resetDelayMin', 'title', 'match', 'field', 'edge'],
  notify: ['title', 'criticality', 'channels'],
  toggle: ['edge', 'title'],
};

export function normalizeCatalogNode(node: CatalogNode): CatalogNode {
  const next: CatalogNode = {
    id: node.id,
    type: node.type,
    x: Number(node.x) || 0,
    y: Number(node.y) || 0,
  };
  for (const key of NODE_FIELDS[node.type] || []) {
    const value = node[key];
    if (value != null && value !== '') {
      (next as Record<string, unknown>)[key] = value;
    }
  }
  return next;
}

export function serializeCatalogFlow(doc: CatalogFlow): string {
  const layouts: string[] = [];
  for (const item of doc.layouts || []) {
    if (item && layouts.indexOf(item) < 0) {
      layouts.push(item);
    }
  }
  for (const node of doc.nodes) {
    if (node.type === 'label' && node.layout && layouts.indexOf(node.layout) < 0) {
      layouts.push(node.layout);
    }
  }
  layouts.sort();
  const body: Record<string, unknown> = {
    id: doc.id,
    enable: doc.enable !== false,
    nodes: doc.nodes.map(normalizeCatalogNode),
    edges: doc.edges.map((edge) => (
      edge.both ? { from: edge.from, to: edge.to, both: true } : { from: edge.from, to: edge.to }
    )),
  };
  if (layouts.length) {
    body.layouts = layouts;
  }
  return `${JSON.stringify(body, null, 2)}\n`;
}

export function portRef(nodeId: string, port: string): string {
  return `${nodeId}.${port}`;
}

export function splitPortRef(ref: string): { id: string; port: string } {
  const idx = ref.indexOf('.');
  if (idx <= 0) {
    return { id: ref, port: '' };
  }
  return { id: ref.slice(0, idx), port: ref.slice(idx + 1) };
}

export function nextCatalogId(prefix: string, nodes: { id: string }[]): string {
  const used = new Set(nodes.map((node) => node.id));
  let n = 1;
  while (used.has(`${prefix}_${n}`)) {
    n += 1;
  }
  return `${prefix}_${n}`;
}

export function controllerFleetId(node: { id: string; owner?: string; hostname?: string }): string {
  if (node.owner) {
    return node.owner.replace(/^g:/, '');
  }
  if (node.id.startsWith('g:')) {
    return node.id.slice(2);
  }
  return node.hostname || node.id;
}
